import { REPORT } from '@consts/reports';
import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { average, count } from '@utils/summaries';
import { basicSort, dateSort } from '@utils/sort';
import { useRange } from '@hooks/useRange';
import { getDataByKey, getUniqueDates, groupBy } from '@utils/data';
import { chart } from '@utils/charts';
import { styleDate, styleDateRange } from '@utils/styles';

interface WarrantyRateByMonthProps {
    dateRange: { startDate: string; endDate: string };
}

export const WarrantyRateByMonth = ({ dateRange }: WarrantyRateByMonthProps) => {
    const [range, setRange] = useRange('WarrantyRateByMonthProduct');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.PRODUCTS_OVERALL_TOTAL_WARRANTY_RATE_BY_MONTH_BY_MATERIAL,
        params: { range, ...dateRange },
    });

    const dateKeys = getUniqueDates(data, range);

    const groupedByDate = groupBy(data, 'date');

    const getData = getDataByKey(dateKeys, groupedByDate);

    const original = { name: 'Original %', data: getData('originalPct') };
    const matte = { name: 'Matte %', data: getData('mattePct') };
    const fusion = { name: 'Fusion %', data: getData('fusionPct') };
    const styleSkins = { name: 'Style Skin %', data: getData('styleSkinPct') };
    const pruvia = { name: 'Pruvia %', data: getData('pruviaPct') };

    return (
        <BaseReport
            setRange={setRange}
            title={'Warranty Rate by Month'}
            description={'This graph shows your overall warranty rate by month for each material type.'}
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            range={range}
            loading={loading}
            hasData={hasData}
            tableOptions={{
                headers: ['Date', 'Original %', 'Matte %', 'Style Skins %', 'Fusion %', 'Pruvia %'],
                data: data.map((item) => [
                    item.date,
                    item.originalPct,
                    item.mattePct,
                    item.styleSkinPct,
                    item.fusionPct,
                    item.pruviaPct,
                ]),
                summaries: [count, average, average, average, average, average],
                style: [styleDate(range)],
                sort: [dateSort(range), basicSort, basicSort, basicSort, basicSort, basicSort],
            }}
            chartOptions={chart(
                300,
                [{ name: 'Warranty %', opposite: false }],
                dateKeys,
                [original, matte, fusion, styleSkins, pruvia],
                undefined,
                {
                    legend: {
                        align: 'right',
                        verticalAlign: 'top',
                    },
                }
            )}
        />
    );
};
